import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { contentAPI, doctorAPI, medicineAPI, consultationAPI, partnerAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';
import { haptics } from '../../../utils/haptics';
import { HomeSkeleton } from '../../../components/SkeletonLoaders';
import ComingSoonCard from '../../../components/ComingSoonCard';

const VideoModal = ({ video, onClose }) => {
  if (!video) return null;

  const getEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('youtube.com/watch?v=')) {
      const id = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    return url;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 animate-fade-in">
      <div className="relative w-full max-w-4xl bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-yellow-500/30">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="aspect-video w-full bg-black">
          <iframe
            src={getEmbedUrl(video.video_url)}
            title={video.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-bold text-yellow-500 mb-2">{video.title}</h2>
          <p className="text-gray-400 text-sm">{video.description}</p>
        </div>
      </div>
    </div>
  );
};

const PatientHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [sliders, setSliders] = useState([]);
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const [approvedDoctors, setApprovedDoctors] = useState([]);
  const [hospitalDoctors, setHospitalDoctors] = useState([]);
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  const [yogaVideos, setYogaVideos] = useState([]);
  const [foodLinks, setFoodLinks] = useState([]);
  const [orgPartners, setOrgPartners] = useState([]);
  const [wellnessPartners, setWellnessPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomePageData();

    let touchStart = 0;
    let touchEnd = 0;

    const handleTouchStart = (e) => {
      touchStart = e.targetTouches[0].clientY;
    };

    const handleTouchMove = (e) => {
      touchEnd = e.targetTouches[0].clientY;
    };

    const handleTouchEnd = () => {
      if (touchStart - touchEnd < -150 && window.scrollY <= 0) {
        haptics.impact('LIGHT');
        fetchHomePageData();
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect non-subscribed users to subscription page
  // Redirect logic removed to allow browsing. Specific actions will require subscription.
  /*
  useEffect(() => {
    if (!loading && user && user.subscription_status !== 'active') {
      navigate('/patient/subscription');
    }
  }, [loading, user, navigate]);
  */

  const fetchHomePageData = async () => {
    try {
      const results = await Promise.allSettled([
        contentAPI.getSliders(),
        doctorAPI.getApprovedDoctors(),
        doctorAPI.getHospitalDoctors(),
        doctorAPI.getClinicDoctors(),
        medicineAPI.getApprovedMedicines(),
        contentAPI.getPodcasts(),
        contentAPI.getYogaSessions(),
        contentAPI.getFoodLinks(),
        partnerAPI.getPublic('organization'),
        partnerAPI.getPublic('wellness'),
        (user?._id || user?.id) ? consultationAPI.checkLimits(user._id || user.id) : Promise.resolve(null)
      ]);

      const [slidersRes, doctorsRes, hospitalRes, clinicRes, medicinesRes,
             podcastsRes, yogaRes, foodRes, orgRes, wellnessRes, limitsRes] = results;

      if (slidersRes.status === 'fulfilled' && slidersRes.value?.data?.success)
        setSliders(slidersRes.value.data.data || []);

      if (doctorsRes.status === 'fulfilled' && doctorsRes.value?.data?.success)
        setApprovedDoctors(doctorsRes.value.data.data?.approved_doctors || []);

      if (hospitalRes.status === 'fulfilled' && hospitalRes.value?.data?.success)
        setHospitalDoctors(hospitalRes.value.data.data?.hospital_doctors || []);

      if (clinicRes.status === 'fulfilled' && clinicRes.value?.data?.success)
        setClinicDoctors(clinicRes.value.data.data?.clinic_doctors || []);

      if (medicinesRes.status === 'fulfilled' && medicinesRes.value?.data?.success)
        setMedicines(medicinesRes.value.data.data || []);

      if (podcastsRes.status === 'fulfilled' && podcastsRes.value?.data?.success)
        setPodcasts(podcastsRes.value.data.data || []);

      if (yogaRes.status === 'fulfilled' && yogaRes.value?.data?.success)
        setYogaVideos(yogaRes.value.data.data || []);

      if (foodRes.status === 'fulfilled' && foodRes.value?.data?.success)
        setFoodLinks(foodRes.value.data.data || []);

      if (orgRes.status === 'fulfilled' && orgRes.value?.data?.success)
        setOrgPartners(orgRes.value.data.data || []);

      if (wellnessRes.status === 'fulfilled' && wellnessRes.value?.data?.success)
        setWellnessPartners(wellnessRes.value.data.data || []);

      if (limitsRes.status === 'fulfilled' && limitsRes.value?.data?.success)
        setSubscriptionLimits(limitsRes.value.data.data);

    } catch (error) {
      showError('Failed to load home page data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sliders.length > 0) {
      const interval = setInterval(() => {
        setCurrentSliderIndex((prev) => (prev + 1) % sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliders.length]);

  const bookSession = (doctorId) => {
    // Check subscription status first
    if (!user.subscription_status || user.subscription_status !== 'active') {
      showError('Please subscribe to book sessions');
      return;
    }
    haptics.impact('MEDIUM');
    navigate(`/patient/consultation/book/${doctorId}`);
  };

  // Video Player Modal Component

  if (loading) {
    return <HomeSkeleton />;
  }

  return (
    <div className="space-y-6 relative pb-20">
      {selectedPodcast && (
        <VideoModal
          video={selectedPodcast}
          onClose={() => setSelectedPodcast(null)}
        />
      )}

      {/* Admin Sliders - Carousel */}
      <section className="pt-2">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gray-900 shadow-2xl border border-white/5">
          {sliders.length > 0 ? (
            <>
              <div className="aspect-[16/9] bg-gray-800 relative">
                {sliders.map((slider, index) => (
                  <div
                    key={slider._id || index}
                    className={`absolute inset-0 transition-opacity duration-500 ${index === currentSliderIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                  >
                    {slider.link_url ? (
                      <a href={slider.link_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={formatImageUrl(slider.image_url)}
                          alt={slider.title}
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ) : (
                      <img
                        src={formatImageUrl(slider.image_url)}
                        alt={slider.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                      <h3 className="text-white font-bold">{slider.title}</h3>
                      {slider.description && (
                        <p className="text-gray-300 text-sm">{slider.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {sliders.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {sliders.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSliderIndex(index)}
                      className={`w-2 h-2 rounded-full ${index === currentSliderIndex ? 'bg-yellow-500' : 'bg-gray-500'
                        }`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-video bg-gray-700 flex items-center justify-center">
              <p className="text-gray-400">No featured content available</p>
            </div>
          )}
        </div>
      </section>

      {/* Quick Action Dashboard */}
      <div className="space-y-4">
        {/* Expert Session Banner */}
        <div
          onClick={() => navigate('/patient/doctors')}
          className="bg-gradient-to-r from-yellow-500 to-amber-600 p-5 rounded-[2rem] flex items-center justify-between cursor-pointer border border-white/10 shadow-2xl hover:shadow-yellow-500/20 transition-all hover:scale-[1.01] active:scale-95 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center border border-dark/10 shadow-inner group-hover:bg-white transition-colors">
              <svg className="w-8 h-8 text-dark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <polyline points="17 11 19 13 23 9" />
              </svg>
            </div>
            <div>
              <h3 className="text-dark font-black text-sm uppercase tracking-tight leading-none">
                Get help<br />through expert specialists
              </h3>
            </div>
          </div>
          <button onClick={() => navigate('/patient/doctors')} className="bg-dark text-yellow-500 px-5 py-3 rounded-2xl font-black text-[10px] uppercase shadow-lg tracking-widest whitespace-nowrap hover:bg-white hover:text-dark transition-all">
            Book Now
          </button>
        </div>

        {/* My Subscriptions Status Card */}
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl p-7 rounded-[2.5rem] border border-white/5 space-y-4 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full -mr-10 -mt-10 blur-2xl group-hover:bg-yellow-500/10 transition-colors"></div>

          <div className="flex justify-between items-center relative z-10">
            <h3 className="text-white font-bold text-xl tracking-tight">Active Plans</h3>
            <span className="text-yellow-500 text-[9px] uppercase tracking-[0.2em] font-black bg-yellow-500/10 px-3 py-1.5 rounded-full border border-yellow-500/20">Active</span>
          </div>

          <div className="space-y-1 bg-white/5 p-5 rounded-2xl relative z-10 border border-white/5">
            <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest opacity-60">
              {user?.subscription_plan_id?.name || (user?.subscription_status === 'active' ? 'Apple Plan' : 'Standard')} Plan • {user?.subscription_end_date ? `Expires ${new Date(user.subscription_end_date).toLocaleDateString()}` : 'No expiry date set'}
            </p>
            <div className="flex gap-4 mt-2">
              <div className="flex flex-col">
                <span className="text-white font-black text-lg">{subscriptionLimits?.video_remaining || 0}</span>
                <span className="text-gray-500 text-[9px] font-bold uppercase tracking-wider">Video</span>
              </div>
              <div className="w-px h-8 bg-white/10 mx-1 self-center"></div>
              <div className="flex flex-col">
                <span className="text-white font-black text-lg">{subscriptionLimits?.chat_remaining || 0}</span>
                <span className="text-gray-500 text-[9px] font-bold uppercase tracking-wider">Chat</span>
              </div>
              <div className="w-px h-8 bg-white/10 mx-1 self-center"></div>
              <div className="flex flex-col">
                <span className="text-white font-black text-lg">{subscriptionLimits?.audio_remaining || 0}</span>
                <span className="text-gray-500 text-[9px] font-bold uppercase tracking-wider">Voice</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/patient/subscription')}
            className="w-full bg-yellow-500 text-dark py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg hover:bg-yellow-400 transition-all active:scale-[0.98] relative z-10"
          >
            Upgrade Plan
          </button>
        </div>

        {/* Quick Actions Grid (2x2) */}
        <div className="grid grid-cols-2 gap-4">
          <div
            onClick={() => navigate('/patient/ambulance')}
            className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/5 flex flex-col items-center text-center cursor-pointer hover:bg-white/5 transition-all group shadow-xl"
          >
            <div className="text-yellow-500 mb-3 transition-transform group-hover:scale-110 group-hover:-translate-y-1">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.129-1.125V11.25a9 9 0 0 0-9-9h-2.25l-2.25 1.5v6.375" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9" />
              </svg>
            </div>
            <h4 className="text-white font-black text-[10px] uppercase tracking-wider">Emergency Support</h4>
            <div className="mt-1 px-2 py-0.5 bg-white/5 rounded-full">
              <p className="text-gray-500 text-[8px] font-bold uppercase tracking-widest italic opacity-60">24/7 Available</p>
            </div>
          </div>

          <div
            onClick={() => navigate('/patient/medicines')}
            className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/5 flex flex-col items-center text-center cursor-pointer hover:bg-white/5 transition-all group shadow-xl"
          >
            <div className="text-yellow-500 mb-3 transition-transform group-hover:scale-110 group-hover:-translate-y-1">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5h-18M21 12H3m18 4.5H3M19.5 4.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm-12 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
              </svg>
            </div>
            <h4 className="text-white font-black text-[10px] uppercase tracking-wider">Store Delivery</h4>
            <div className="mt-1 px-2 py-0.5 bg-white/5 rounded-full">
              <p className="text-gray-500 text-[8px] font-bold uppercase tracking-widest opacity-60">Always Active</p>
            </div>
          </div>

          <div
            onClick={() => navigate('/patient/podcasts')}
            className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/5 flex flex-col items-center text-center cursor-pointer hover:bg-white/5 transition-all group shadow-xl"
          >
            <div className="text-yellow-500 mb-3 transition-transform group-hover:scale-110 group-hover:-translate-y-1">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <h4 className="text-white font-black text-[10px] uppercase tracking-wider">My Reports</h4>
            <div className="mt-1 px-2 py-0.5 bg-white/5 rounded-full">
              <p className="text-gray-500 text-[8px] font-bold uppercase tracking-widest opacity-60">View History</p>
            </div>
          </div>

          <div
            onClick={() => navigate('/patient/yoga')}
            className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/5 flex flex-col items-center text-center cursor-pointer hover:bg-white/5 transition-all group shadow-xl"
          >
            <div className="text-yellow-500 mb-3 transition-transform group-hover:scale-110 group-hover:-translate-y-1">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.048 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 0 0 .495-7.467 5.99 5.99 0 0 0-1.925 3.546 5.974 5.974 0 0 1-2.133-1A3.75 3.75 0 0 0 12 18z" />
              </svg>
            </div>
            <h4 className="text-white font-black text-[10px] uppercase tracking-wider">Yoga Classes</h4>
            <div className="mt-1 px-2 py-0.5 bg-white/5 rounded-full">
              <p className="text-gray-500 text-[8px] font-bold uppercase tracking-widest opacity-60">5 classes inc.</p>
            </div>
          </div>

          <div
            onClick={() => navigate('/patient/food-links')}
            className="bg-white/5 border border-white/10 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/5 flex flex-col items-center text-center cursor-pointer hover:bg-white/5 transition-all group shadow-xl"
          >
            <div className="text-yellow-500 mb-3 transition-transform group-hover:scale-110 group-hover:-translate-y-1">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v17.792c0 .61.494 1.104 1.104 1.104h2.292c.61 0 1.104-.494 1.104-1.104V3.104c0-.61-.494-1.104-1.104-1.104h-2.292c-.61 0-1.104.494-1.104 1.104z" />
              </svg>
            </div>
            <h4 className="text-white font-black text-[10px] uppercase tracking-wider">Nutrition Plans</h4>
            <div className="mt-1 px-2 py-0.5 bg-white/5 rounded-full">
              <p className="text-gray-500 text-[8px] font-bold uppercase tracking-widest opacity-60">1 plan inc.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Approved Doctors List */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Available Specialists</h2>
          <button
            onClick={() => navigate('/patient/doctors')}
            className="text-yellow-500 text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="space-y-3">
          {approvedDoctors.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <p>No available specialists at the moment</p>
            </div>
          ) : (
            approvedDoctors.slice(0, 3).map((doctor, index) => (
              <div key={doctor._id || index} className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{doctor.name}</h3>
                    <p className="text-gray-400 text-sm">{doctor.specialization}</p>
                    <p className="text-gray-400 text-sm">Experience: {doctor.experience || 'N/A'} years</p>
                  </div>
                  <button
                    onClick={() => bookSession(doctor._id)}
                    className="bg-yellow-500 text-dark px-4 py-2 rounded-lg font-medium text-sm"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Approved Medicines */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Featured Products</h2>
          <button
            onClick={() => navigate('/patient/medicines')}
            className="text-yellow-500 text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {medicines.length === 0 ? (
            <div className="col-span-2 text-center py-6 text-gray-400">
              <p>No products available yet</p>
            </div>
          ) : (
            medicines.slice(0, 4).map((medicine, index) => (
              <div key={medicine._id || index} className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-4">
                <h3 className="font-semibold text-sm mb-1">{medicine.name}</h3>
                {medicine.description && (
                  <p className="text-gray-400 text-xs mb-2 line-clamp-2">{medicine.description}</p>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-yellow-500 font-bold">₹{medicine.price}</span>
                  {medicine.stock_quantity !== undefined && (
                    <span className="text-gray-400 text-xs">
                      {medicine.stock_quantity > 0 ? `In Stock` : 'Out of Stock'}
                    </span>
                  )}
                </div>
                {medicine.retailer_id && (
                  <p className="text-gray-500 text-xs mt-1">Retailer: {medicine.retailer_id.name}</p>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Organization Partners */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Organization Partners</h2>
          <button
            onClick={() => navigate('/patient/doctors')}
            className="text-yellow-500 text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="space-y-3">
          {orgPartners.length === 0 ? (
            <ComingSoonCard
              title="Organization Partners"
              icon="🏢"
              description="Hospital and organization partnerships coming soon."
            />
          ) : (
            orgPartners.slice(0, 3).map((partner) => (
              <div key={partner._id} className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  {partner.image_url && (
                    <img src={formatImageUrl(partner.image_url)} alt={partner.name} className="w-12 h-12 rounded-lg object-cover" loading="lazy" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold">{partner.name}</h3>
                    {partner.description && <p className="text-gray-400 text-sm">{partner.description}</p>}
                  </div>
                  {partner.link_url && (
                    <a href={partner.link_url} target="_blank" rel="noopener noreferrer" className="bg-yellow-500 text-dark px-3 py-1.5 rounded-lg text-sm font-medium">
                      Visit
                    </a>
                  )}
                </div>
                {partner.status === 'coming_soon' && (
                  <span className="inline-block mt-2 text-xs bg-yellow-500 text-dark px-2 py-0.5 rounded-full font-semibold">Coming Soon</span>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Wellness Partners */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Wellness Centers</h2>
          <button
            onClick={() => navigate('/patient/doctors')}
            className="text-yellow-500 text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="space-y-3">
          {wellnessPartners.length === 0 ? (
            <ComingSoonCard
              title="Wellness Centers"
              icon="🌿"
              description="Wellness and clinic partnerships coming soon."
            />
          ) : (
            wellnessPartners.slice(0, 3).map((partner) => (
              <div key={partner._id} className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  {partner.image_url && (
                    <img src={formatImageUrl(partner.image_url)} alt={partner.name} className="w-12 h-12 rounded-lg object-cover" loading="lazy" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold">{partner.name}</h3>
                    {partner.description && <p className="text-gray-400 text-sm">{partner.description}</p>}
                  </div>
                  {partner.link_url && (
                    <a href={partner.link_url} target="_blank" rel="noopener noreferrer" className="bg-yellow-500 text-dark px-3 py-1.5 rounded-lg text-sm font-medium">
                      Visit
                    </a>
                  )}
                </div>
                {partner.status === 'coming_soon' && (
                  <span className="inline-block mt-2 text-xs bg-yellow-500 text-dark px-2 py-0.5 rounded-full font-semibold">Coming Soon</span>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Free Video Podcasts */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Expert Podcasts</h2>
          <button onClick={() => navigate('/patient/podcasts')} className="text-yellow-500 text-sm hover:underline">View All</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {podcasts.slice(0, 4).map((podcast, index) => (
            <div
              key={podcast._id || index}
              onClick={() => setSelectedPodcast(podcast)}
              className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden block cursor-pointer group"
            >
              <div className="aspect-video bg-gray-700 flex items-center justify-center relative">
                {podcast.thumbnail ? (
                  <img
                    src={formatImageUrl(podcast.thumbnail)}
                    alt={podcast.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="bg-gray-700 w-full h-full flex items-center justify-center">
                    <span className="text-gray-500">No Preview</span>
                  </div>
                )}
                <div className="absolute bg-yellow-500/80 text-dark rounded-full p-2 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium text-sm line-clamp-2 text-yellow-500">{podcast.title}</h3>
                <p className="text-gray-400 text-xs mt-1">{podcast.category || 'Podcast'}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Paid Yoga Live Videos */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-yellow-500">Yoga Classes</h2>
          <button
            onClick={() => navigate('/patient/yoga')}
            className="text-yellow-500 text-sm hover:underline"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {yogaVideos.length === 0 ? (
            <div className="col-span-2 text-center py-6 text-gray-400">
              <p>No yoga videos available</p>
            </div>
          ) : (
            yogaVideos.slice(0, 4).map((video, index) => (
              <div key={video._id || index} className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl overflow-hidden">
                <div className="aspect-video bg-gray-700 flex items-center justify-center relative">
                  {video.thumbnail_url ? (
                    <img
                      src={formatImageUrl(video.thumbnail_url)}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : null}

                  {user?.subscription_status === 'active' ? (
                    <button
                      onClick={() => navigate(`/patient/live-class/${video._id}`)}
                      className="absolute bg-yellow-500 text-dark rounded-full p-2 hover:scale-110 transition-transform"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/patient/subscription')}
                      className="absolute bg-gray-900/90 text-white rounded-full p-2 hover:bg-black transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-sm line-clamp-2">{video.title}</h3>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-gray-400 text-xs">₹{video.price || 0}</p>
                    {video.duration && (
                      <p className="text-gray-400 text-xs">{video.duration}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Food Links */}
      <section>
        <h2 className="text-xl font-semibold mb-4 text-yellow-500">Order Food <span className="text-xs bg-yellow-500 text-dark px-2 py-0.5 rounded-full font-bold align-middle ml-2">Coming Soon</span></h2>
        <div className="grid grid-cols-2 gap-3">
          {foodLinks.length === 0 ? (
            <div className="col-span-2 text-center py-6 text-gray-400">
              <p>No food links available</p>
            </div>
          ) : (
            foodLinks.slice(0, 4).map((link, index) => (
              <a
                key={link._id || index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-4 text-center hover:bg-gray-700 transition-colors"
              >
                {link.image_url ? (
                  <img
                    src={formatImageUrl(link.image_url)}
                    alt={link.title}
                    className="w-12 h-12 mx-auto mb-2 rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-dark font-bold text-lg">
                      {link.platform === 'swiggy' ? 'S' : link.platform === 'zomato' ? 'Z' : 'F'}
                    </span>
                  </div>
                )}
                <h3 className="font-medium">{link.title}</h3>
                {link.description && (
                  <p className="text-gray-400 text-xs mt-1">{link.description}</p>
                )}
              </a>
            ))
          )}
        </div>
      </section>


    </div>
  );
};

export default PatientHome;
